import type { SanityCaseStudyMetadata, SanityReferenceSummary } from "@ouster/shared";
import type { IntroDetailsItemView } from "~/features/blocks/inventory/intro-details";
import { formatCaseStudyLocation } from "~/features/case-study/case-study-meta";
import { cleanStegaForTextTransform } from "~/features/sanity/stega";
function label(count: number, singular: string, plural: string) {
  return count === 1 ? singular : plural;
}
function toInternalHref(uri?: string | null) {
  if (!uri) return undefined;
  const cleanUri = cleanStegaForTextTransform(uri);
  // eslint-disable-next-line no-control-regex -- Reject control characters in CMS-authored paths.
  if (/[\\\u0000-\u001f\u007f]/.test(cleanUri)) return undefined;
  const path = cleanUri.trim();
  return path ? `/${path.replace(/^\/+/, "")}` : undefined;
}
function toValues(refs?: SanityReferenceSummary[] | null, linked = true) {
  return (refs ?? []).filter((ref): ref is SanityReferenceSummary => Boolean(ref?.title?.trim())).map(ref => ({
    text: ref.title,
    href: linked ? toInternalHref(ref.uri) : undefined
  }));
}

/** Authored rows precede the document's location, product and industry rows. */
export function buildCaseStudyIntroRows(metadata?: SanityCaseStudyMetadata | null): IntroDetailsItemView[] {
  if (!metadata) return [];
  const locations = (metadata.locations ?? []).map(location => formatCaseStudyLocation({
    city: location?.city?.trim() || undefined,
    state: location?.state?.trim() || undefined,
    country: location?.country?.trim() || undefined
  })).filter((value): value is string => Boolean(value));
  const products = toValues(metadata.hardwareProducts);
  // Industry categories are taxonomy; their slugs do not reliably resolve to pages.
  const industries = toValues(metadata.industries, false);
  return [...(metadata.details ?? []).filter(detail => Boolean(detail?.label?.trim() || detail?.text?.trim())).map(detail => ({
    label: detail.label,
    text: detail.text
  })), ...(locations.length ? [{
    label: label(locations.length, "Location", "Locations"),
    text: locations.join(" · ")
  }] : []), ...(products.length ? [{
    label: label(products.length, "Product Used", "Products Used"),
    values: products
  }] : []), ...(industries.length ? [{
    label: label(industries.length, "Industry", "Industries"),
    values: industries
  }] : [])];
}